import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { INotification, NotificationButton } from 'interfaces/notification';
import api from 'constants/api';
import Api from 'util/Api';
import { requestForToken } from 'util/firebase';
import { message } from 'antd';

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
  filters?: any;
}

interface PaginatedResult<T> {
  data: T[];
  meta: PaginationMeta;
}

export interface GetNotificationsParams {
  status?: 'seen' | 'unseen';
  page?: number;
  limit?: number;
  fromDate?: string;
  toDate?: string;
  searchTerm?: string;
}

export interface NotificationFilters {
  fromDate?: Date;
  toDate?: Date;
  page?: number;
  limit?: number;
  searchTerm?: string;
  excludeDeleted?: boolean;
}

export const NotificationTypes = {
  ROUTE_ASSIGN_REQUEST: 'ROUTE_ASSIGN_REQUEST',
  ROUTE_RECALCULATED: 'ROUTE_RECALCULATED',
  GENERAL: 'GENERAL',
  FROM_OPERATIONS: 'FROM_OPERATIONS',
};

export type NotificationActionType = 'DISMISS' | 'URL';
export interface NotificationAction {
  type: NotificationActionType;
  objectiveId?: string;
}

export type NotificationType = keyof typeof NotificationTypes;
export interface NotificationImage {
  src: string;
  shape: 'SQUARE' | 'CIRCULAR';
}

export type NotificationButtonType = 'PRIMARY' | 'SECONDARY';

type NotificationStatus = 'seen' | 'unseen' | 'deleted';

export const NotificationScope = {
  DALI_MOBILE: 'DALI_MOBILE',
} as const;

export const NotificationIconTypes = {
  APPROVED: 'APPROVED',
  DENIED: 'DENIED',
  CLOCK: 'CLOCK',
};

export interface FormattedNotification {
  id: string;
  title: string;
  description?: string;
  type?: NotificationType;
  images?: {
    main?: NotificationImage;
    left?: NotificationImage[];
    right?: NotificationImage[];
  };
  buttons?: NotificationButton[];
  action?: NotificationAction;
  icon?: keyof typeof NotificationIconTypes;
  scope?: keyof typeof NotificationScope;
  url?: string;
  status: 'seen' | 'unseen' | 'deleted';
  seenTimestamp: string | null;
  creationTimestamp: Date;
  updatedAt: Date;
}

export interface FormattedCreatedNotification {
  id: string;
  title: string;
  description?: string;
  type?: NotificationType;
  images?: {
    main?: NotificationImage;
    left?: NotificationImage[];
    right?: NotificationImage[];
  };
  buttons?: {
    title: string;
    type: NotificationButtonType;
    action: NotificationAction;
  }[];
  action?: NotificationAction;
  icon?: keyof typeof NotificationIconTypes;
  scope?: keyof typeof NotificationScope;
  url?: string;
  status: 'seen' | 'unseen' | 'deleted';
  seenTimestamp: string | null;
  creationTimestamp: Date;
  updatedAt: Date;
  recipients: {
    [key: string]: {
      recipientID: string;
      status: string;
      seenTimestamp?: string;
    };
  };
}

const INITIAL_META = {
  total: 0,
  page: 1,
  limit: 20,
  pages: 1,
};

const APIKEY = process.env.REACT_APP_FIREBASE_API_KEY as string;

export async function refreshFCMToken(authToken: string) {
  if (!authToken) {
    console.error('❌ No auth token provided');
    return undefined;
  }

  try {
    console.log('🔄 Refreshing FCM token...');
    const token = await requestForToken();

    if (!token) {
      console.error('❌ Failed to get FCM token');
      return undefined;
    }

    console.log('📤 Sending token to backend...');
    const body = {
      fcmToken: token,
      scope: 'WEBAPP_PPOINT',
    };

    const response = await fetch(api.notifications.refreshToken, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        api_key: APIKEY,
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(body),
    });

    console.log(`📡 Backend response status: ${response.status}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        '❌ Backend response error:',
        response.status,
        response.statusText,
        errorText,
      );
      return undefined;
    }

    const responseText = await response.text();
    console.log('✅ FCM token registered successfully with backend');
    console.log('📋 Backend response:', responseText);

    if (responseText === '{}' || responseText === '') return body;
    try {
      const data = JSON.parse(responseText);
      return data;
    } catch {
      return body;
    }
  } catch (error) {
    console.error('❌ Error refreshing FCM token:', error);
    return undefined;
  }
}

const getNotifications = async (params?: GetNotificationsParams) => {
  try {
    const queryParams = new URLSearchParams({
      status: params?.status || '',
      page: params?.page?.toString() || INITIAL_META.page.toString(),
      limit: params?.limit?.toString() || INITIAL_META.limit.toString(),
      fromDate: params?.fromDate || '',
      toDate: params?.toDate || '',
      searchTerm: params?.searchTerm || '',
    }).toString();
    const response = await Api.apiAxios.get<
      PaginatedResult<FormattedNotification>
    >(`${api.notifications.root}?${queryParams}`);
    if (!Api.isSuccessResponse(response)) {
      throw new Error(`Status: ${response.status}`);
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

export const useNotifications = (params?: GetNotificationsParams) => {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => getNotifications(params),
    initialData: {
      data: [],
      meta: INITIAL_META,
    },
  });
};

export const useUnreadsNotifications = () => {
  return useQuery({
    queryKey: ['notifications-unreads'],
    queryFn: () =>
      getNotifications({
        limit: 10,
        page: 1,
        status: 'unseen',
      }),
    refetchInterval: 60000, // Refetch every 60 seconds
    initialData: {
      data: [],
      meta: INITIAL_META,
    },
  });
};

/**
 * Custom hook for infinite scrolling notifications
 * @param params - Query parameters for notifications
 * @returns Infinite query result with notifications data and helper functions
 */
export const useInfiniteNotifications = (params?: GetNotificationsParams) => {
  return useInfiniteQuery({
    queryKey: ['infiniteNotifications', params],
    queryFn: ({ pageParam = 1 }) =>
      getNotifications({
        ...params,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      // If we're on the last page, return undefined to signal no more pages
      if (lastPage.meta.page >= lastPage.meta.pages) {
        return undefined;
      }
      // Otherwise return the next page number
      return lastPage.meta.page + 1;
    },
    select: (data) => {
      // Flatten the pages array and combine all notifications
      const flatData = data.pages.flatMap((page) => page.data);
      // Get the meta from the latest page
      const meta = data.pages[data.pages.length - 1]?.meta || INITIAL_META;
      return {
        data: flatData,
        meta,
      };
    },
  });
};

/**
 * Mark a notification as seen
 * @param id - The ID of the notification to mark as seen
 * @returns Promise with the result of the operation
 */
const markNotificationSeen = async (id: string | number) => {
  try {
    return await Api.apiAxios.put(api.notifications.markAsSeen(id));
  } catch (error) {}
};

/**
 * Custom hook for marking a notification as seen
 * @returns Mutation object with the markAsSeen function and status
 */
export const useMarkNotificationSeen = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationSeen,
    onMutate: async (notificationId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['notifications'] });
      await queryClient.cancelQueries({ queryKey: ['infiniteNotifications'] });
      // Optimistic update for regular notifications query
      const previousNotifications = queryClient.getQueryData<
        PaginatedResult<INotification>
      >(['notifications', { status: 'unseen' }]);
      if (previousNotifications) {
        queryClient.setQueryData(
          ['notifications', { status: 'unseen' }],
          (old: any) => {
            return {
              ...old,
              data: old.data.filter(
                (notification: INotification) =>
                  notification.id !== notificationId,
              ),
            };
          },
        );
      }
      // Optimistic update for infinite query
      const previousInfiniteData = queryClient.getQueryData<
        InfiniteData<PaginatedResult<INotification>>
      >(['infiniteNotifications', { status: 'unseen' }]);
      if (previousInfiniteData) {
        queryClient.setQueryData(
          ['infiniteNotifications', { status: 'unseen' }],
          (old: any) => {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                data: page.data.filter(
                  (notification: INotification) =>
                    notification.id !== notificationId,
                ),
              })),
            };
          },
        );
      }
      // Add notification to seen queries
      const previousSeenNotifications = queryClient.getQueryData([
        'notifications',
        { status: 'seen' },
      ]);
      const previousInfiniteSeenData = queryClient.getQueryData<
        InfiniteData<PaginatedResult<INotification>>
      >(['infiniteNotifications', { status: 'seen' }]);
      // Find the notification to move
      let notificationToMove: INotification | undefined;
      // Look in the regular query first
      if (previousNotifications?.data) {
        notificationToMove = previousNotifications.data.find(
          (notification: INotification) => notification.id === notificationId,
        );
      }
      // If not found, look in the infinite query
      if (!notificationToMove && previousInfiniteData?.pages) {
        for (const page of previousInfiniteData.pages) {
          const found = page.data.find(
            (notification: INotification) => notification.id === notificationId,
          );
          if (found) {
            notificationToMove = found;
            break;
          }
        }
      }
      // If we found the notification, add it to the seen queries
      if (notificationToMove) {
        const updatedNotification = {
          ...notificationToMove,
          status: 'seen',
          viewed_at: new Date().toISOString(),
        };
        // Update regular seen query if it exists
        if (previousSeenNotifications) {
          queryClient.setQueryData(
            ['notifications', { status: 'seen' }],
            (old: any) => {
              return {
                ...old,
                data: [updatedNotification, ...old.data],
              };
            },
          );
        }
        // Update infinite seen query if it exists
        if (
          previousInfiniteSeenData &&
          previousInfiniteSeenData.pages.length > 0
        ) {
          queryClient.setQueryData(
            ['infiniteNotifications', { status: 'seen' }],
            (old: any) => {
              // Add to the first page
              const newPages = [...old.pages];
              newPages[0] = {
                ...newPages[0],
                data: [updatedNotification, ...newPages[0].data],
              };
              return {
                ...old,
                pages: newPages,
              };
            },
          );
        }
      }
      // Return previous data to roll back if mutation fails
      return {
        previousNotifications,
        previousInfiniteData,
        previousSeenNotifications,
        previousInfiniteSeenData,
      };
    },
    onError: (_, _p, context) => {
      // Roll back optimistic updates if mutation fails
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ['notifications', { status: 'unseen' }],
          context.previousNotifications,
        );
      }
      if (context?.previousInfiniteData) {
        queryClient.setQueryData(
          ['infiniteNotifications', { status: 'unseen' }],
          context.previousInfiniteData,
        );
      }
      if (context?.previousSeenNotifications) {
        queryClient.setQueryData(
          ['notifications', { status: 'seen' }],
          context.previousSeenNotifications,
        );
      }
      if (context?.previousInfiniteSeenData) {
        queryClient.setQueryData(
          ['infiniteNotifications', { status: 'seen' }],
          context.previousInfiniteSeenData,
        );
      }
      message.error('Error al marcar la notificación como leída');
    },
    onSettled: () => {
      // Always invalidate queries after mutation settles (success or error)
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['infiniteNotifications'] });
    },
  });
};

const markAllAsSeen = async () => {
  try {
    return await Api.apiAxios.put(api.notifications.markAllAsSeen);
  } catch (error) {
    console.error('Error marking all notifications as seen:', error);
    throw error;
  }
};

export const useMarkAllNotificationsAsSeen = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllAsSeen,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });
      await queryClient.cancelQueries({ queryKey: ['infiniteNotifications'] });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['infiniteNotifications'] });
      message.success('Todas las notificaciones han sido marcadas como leídas');
    },
    onError: () => {
      message.error(
        'Ocurrió un error al marcar las notificaciones como leídas',
      );
    },
  });
};

// Outbound Messages (Created Notifications) Functions
const getOutboundMessages = async (filters?: NotificationFilters) => {
  try {
    const queryParams = new URLSearchParams({
      page: filters?.page?.toString() || INITIAL_META.page.toString(),
      limit: filters?.limit?.toString() || INITIAL_META.limit.toString(),
      fromDate: filters?.fromDate?.toISOString() || '',
      toDate: filters?.toDate?.toISOString() || '',
      searchTerm: filters?.searchTerm || '',
      excludeDeleted: filters?.excludeDeleted?.toString() || 'true',
    }).toString();

    const response = await Api.apiAxios.get<
      PaginatedResult<FormattedCreatedNotification>
    >(`${api.notifications.created}?${queryParams}`);
    if (!Api.isSuccessResponse(response)) {
      throw new Error(`Status: ${response.status}`);
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching outbound messages:', error);
    throw error;
  }
};

export const useOutboundMessages = (filters?: NotificationFilters) => {
  return useQuery({
    queryKey: ['outboundMessages', filters],
    queryFn: () => getOutboundMessages(filters),
    initialData: {
      data: [],
      meta: INITIAL_META,
    },
  });
};

// Create Notification Interface
export interface CreateNotificationRequest {
  push: boolean;
  title: string;
  description: string;
  type: string;
  sendEmail: boolean;
  images?: {
    main?: NotificationImage;
    left?: NotificationImage[];
    right?: NotificationImage[];
  };
  buttons?: NotificationButton[];
  action?: NotificationAction;
  icon: string;
  scope: string;
  url?: string;
  recipients: string[];
}

// Create notification function
const createNotification = async (
  notificationData: CreateNotificationRequest,
) => {
  try {
    const { data } = await Api.apiAxios.post(
      api.notifications.root,
      notificationData,
    );
    return data;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

export const useCreateNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createNotification,
    onSuccess: () => {
      // Invalidate and refetch outbound messages
      queryClient.invalidateQueries({ queryKey: ['outboundMessages'] });
    },
    onError: (error) => {
      message.error('Error al crear la notificación ' + error);
    },
  });
};
